import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TrailDocumentCapability } from '../../dto/trail-document-capability';
import { TrailDocumentCapabilityService } from '../../service/trail-document-capability.service';
import { Trail } from '../../dto/trail';
import { TrailDocumentCapabilityDetailComponent } from '../trail-document-capability-detail/trail-document-capability-detail.component';

@Component({
  selector: 'dpi-trail-document-capability',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule,
    MatTooltipModule, NgIf],
  templateUrl: './trail-document-capability.component.html',
  styleUrl: './trail-document-capability.component.scss'
})
export class TrailDocumentCapabilityComponent implements OnInit, OnDestroy {
  trail: Trail;
  displayedColumns: string[] = ['capability_plot_name', 'capability_plot_remark', 'verifying_footprint_name', 'verifying_footprint_remark'];
  dataSource: TrailDocumentCapability[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private trailDocumentCapabilityService: TrailDocumentCapabilityService) {
    this.trail = this.notifyService.getSelectedTrail();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.trail = model;
        this.reload(model);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reload(selectedTrail: Trail) {
    this.trailDocumentCapabilityService.getAllSetupOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  selectTrailDocumentCapability(selected: TrailDocumentCapability) {
    if (!this.isReadonly) {
      var objCopy: TrailDocumentCapability = { ...selected };
      const dialogRef = this._dialog.open(TrailDocumentCapabilityDetailComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.trailDocumentCapability);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.trailDocumentCapability.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  create() {
    this.trailDocumentCapabilityService.createDocumentCapability(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectTrailDocumentCapability(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: TrailDocumentCapability) {
    let list: TrailDocumentCapability[] = [];
    for (var item of this.dataSource) {
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  update(model: TrailDocumentCapability) {
    this.trailDocumentCapabilityService.updateDocumentCapability(model)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.replaceModelInDataSource(res);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: TrailDocumentCapability) {
    let list: TrailDocumentCapability[] = [];
    for (var item of this.dataSource) {
      if (item.id === model.id) {
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  delete(modelId: number) {
    this.trailDocumentCapabilityService.deleteDocumentCapability(modelId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(modelId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(modelId: number) {
    let list: TrailDocumentCapability[] = [];
    for (var item of this.dataSource) {
      if (item.id === modelId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }


}
